import {announce} from '@github-ui/aria-live'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ReadonlySectionHeader} from '@github-ui/issue-metadata/ReadonlySectionHeader'
import {Section} from '@github-ui/issue-metadata/Section'
import {useQueryClient} from '@github-ui/react-query'
import {useAlive} from '@github-ui/use-alive'
import {BellIcon, BellSlashIcon} from '@primer/octicons-react'
import {Button, Heading} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useRef, useState} from 'react'

import {ALIVE_REASONS} from '../../../constants/alive'
import {LABELS} from '../../../constants/labels'
import {useUpdateIssueSubscription} from '../../../mutations/use-update-issue-subscription'
import type {IssueSecondaryNode} from '../../issue-viewer-secondary-query-types'
import type {IssueViewerViewerFragment} from '../../IssueViewer'
import {type SubscriptionStatus, subscriptionStatusQueryKey} from './fetch-subscription-status'
import {NotificationSettingsDialog} from './NotificationSettingsDialog'
import styles from './SubscriptionSection.module.css'
import {useSubscriptionStatus} from './use-subscription-status'

// Plain fields derived from the allowlisted `IssueViewerSecondaryViewQuery`.
// `repository`/`owner` are narrowed to the fields this section selects.
export type SubscriptionSectionFragment = Pick<
  IssueSecondaryNode,
  | 'id'
  | 'number'
  | 'threadSubscriptionChannel'
  | 'viewerThreadSubscriptionFormAction'
  | 'viewerCustomSubscriptionEvents'
> & {
  repository: Pick<IssueSecondaryNode['repository'], 'name'> & {
    owner: Pick<IssueSecondaryNode['repository']['owner'], 'login'>
  }
}

export type SubscriptionSectionProps = {
  issue: SubscriptionSectionFragment
  viewer: IssueViewerViewerFragment | null
}

export function SubscriptionSection({issue, viewer}: SubscriptionSectionProps) {
  const {
    threadSubscriptionChannel,
    id,
    number,
    viewerThreadSubscriptionFormAction,
    viewerCustomSubscriptionEvents,
    repository: {
      name: repo,
      owner: {login: owner},
    },
  } = issue

  // threadSubscriptionChannel should not be null for logged-in users, but check to ensure that it is loaded
  // because useAlive expects a valid channel name.
  return threadSubscriptionChannel ? (
    <SubscriptionSectionInternal
      id={id}
      owner={owner}
      repo={repo}
      number={number}
      threadSubscriptionChannel={threadSubscriptionChannel}
      initialFormAction={viewerThreadSubscriptionFormAction ?? null}
      initialCustomEvents={viewerCustomSubscriptionEvents ?? null}
      viewer={viewer}
    />
  ) : null
}

function SubscriptionSectionInternal({
  id,
  owner,
  repo,
  number,
  threadSubscriptionChannel,
  initialFormAction,
  initialCustomEvents,
  viewer,
}: {
  id: string
  owner: string
  repo: string
  number: number
  threadSubscriptionChannel: string
  initialFormAction: SubscriptionStatus['viewerThreadSubscriptionFormAction']
  initialCustomEvents: SubscriptionStatus['viewerCustomSubscriptionEvents']
  viewer: IssueViewerViewerFragment | null
}) {
  const shouldUpdateAnnouncement = useRef(false)
  const {updateSubscription} = useUpdateIssueSubscription({
    issueId: id,
    nameWithOwner: `${owner}/${repo}`,
    issueNumber: number,
  })
  const queryClient = useQueryClient()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [preferencesSaved, setPreferencesSaved] = useState(false)

  const announceSubscriptionUpdates = useCallback((newSubscribed: boolean, success = true) => {
    if (success) {
      const message = newSubscribed
        ? LABELS.notifications.subscribedAnnouncement
        : LABELS.notifications.unsubscribedAnnouncement
      announce(message)
    } else {
      announce(LABELS.somethingWentWrong)
    }
  }, [])

  const data = useSubscriptionStatus(owner, repo, number, {
    id,
    viewerThreadSubscriptionFormAction: initialFormAction,
    viewerCustomSubscriptionEvents: initialCustomEvents,
  })
  const {viewerThreadSubscriptionFormAction, viewerCustomSubscriptionEvents, id: issueId} = data
  const subscribed = viewerThreadSubscriptionFormAction === 'UNSUBSCRIBE'
  // Spreading this because it's 'readonly' and cannot be assigned to the mutable type 'ThreadSubscriptionEvent[]' when passing it to the Dialog
  const preSelectedCustomOptions = viewerCustomSubscriptionEvents ? [...viewerCustomSubscriptionEvents] : []

  const refreshSubscription = useCallback(() => {
    shouldUpdateAnnouncement.current = true
    void queryClient.invalidateQueries({queryKey: subscriptionStatusQueryKey(owner, repo, number)})
  }, [queryClient, owner, repo, number])

  useEffect(() => {
    if (shouldUpdateAnnouncement.current) {
      announceSubscriptionUpdates(subscribed)
      shouldUpdateAnnouncement.current = false
    }
  }, [subscribed, announceSubscriptionUpdates])

  const toggleSubscription = useCallback(() => {
    const newSubscribed = !subscribed
    const newSubscriptionState = newSubscribed ? 'SUBSCRIBED' : 'UNSUBSCRIBED'
    sendEvent('notifications.thread_subscription.toggle', {
      subscriptionState: newSubscriptionState,
      threadClass: 'Issue',
      threadId: id,
    })

    updateSubscription(
      {state: newSubscriptionState},
      {
        onCompleted: () => {
          announceSubscriptionUpdates(newSubscribed)
        },
        onError: () => {
          announceSubscriptionUpdates(subscribed, false)
        },
      },
    )
  }, [updateSubscription, id, subscribed, announceSubscriptionUpdates])

  // Thread subscription updates are scoped to an individual user and emitted via signed user channels.
  useAlive(threadSubscriptionChannel, (event: {reason: string}) => {
    if (!event?.reason) return
    // Refetch subscription information if we receive a message that the subscription has changed.
    // We cannot rely on the "reason" to determine the new state, because we still receive a subscription
    // message if the thread is "ignored".
    if (event.reason === ALIVE_REASONS.SUBSCRIBED && !subscribed) {
      refreshSubscription()
    } else if (event.reason === ALIVE_REASONS.UNSUBSCRIBED && subscribed) {
      refreshSubscription()
    }
  })

  const buttonRef = useRef<HTMLButtonElement>(null)

  const announcePreferencesSavedRef = useRef(false)
  const onPreferencesSaved = useCallback(() => {
    announcePreferencesSavedRef.current = true
    setPreferencesSaved(true)
  }, [])

  // Announce once the dialog has closed and returned focus, so the focus change doesn't cut it off.
  useEffect(() => {
    if (isDialogOpen || !preferencesSaved || !announcePreferencesSavedRef.current) return
    announcePreferencesSavedRef.current = false
    announce(LABELS.notifications.preferencesSaved)
  }, [isDialogOpen, preferencesSaved])

  const sectionHeader = (
    <div className={clsx(styles.container)}>
      <Heading id={id} as="h3" className={clsx(styles.header)}>
        Notifications
      </Heading>
      {/* After a save the label keeps confirming it, including when focus returns here from the dialog. */}
      <button onClick={() => setIsDialogOpen(true)} className={clsx(styles.button)} ref={buttonRef}>
        {preferencesSaved
          ? LABELS.notifications.customizeNotificationsSettingsUpdated
          : LABELS.notifications.customizeNotificationsSettings}
      </button>
      {isDialogOpen && (
        <NotificationSettingsDialog
          title={LABELS.notifications.notificationsSettings}
          onClose={() => setIsDialogOpen(false)}
          onSaved={onPreferencesSaved}
          returnFocusRef={buttonRef}
          threadId={issueId}
          nameWithOwner={`${owner}/${repo}`}
          issueNumber={number}
          preselectedViewerSubscriptionEvents={preSelectedCustomOptions}
          subscribed={subscribed}
        />
      )}
    </div>
  )

  // It controls whether email and web notifications for issues should be sent
  // via notifyd, and whether notifyd should be the primary source of truth
  // for issues related subscriptions and settings.
  const isNotifydEnabledIssuesWatchActivity = isFeatureEnabled('notifyd_issue_watch_activity_notify')

  // Controls whether the settings for sending notifications for
  // participating activity should be written to notify
  const isNotifydEnabledIssuesThreadSubscription = isFeatureEnabled('notifyd_enable_issue_thread_subscriptions')

  const isNotifydCustomSubscriptionEventsEnabled = isFeatureEnabled('notifyd_issue_custom_subscription_events_notify')

  // Notifyd-backed subscriptions support customization only when custom subscription events are enabled.
  const customizeDisabled =
    viewer?.isEnterpriseManagedUser ||
    (!isNotifydCustomSubscriptionEventsEnabled &&
      (isNotifydEnabledIssuesThreadSubscription || isNotifydEnabledIssuesWatchActivity))

  return (
    <Section
      sectionHeader={
        customizeDisabled ? <ReadonlySectionHeader title={LABELS.notifications.notificationsTitle} /> : sectionHeader
      }
    >
      <div className="width-full d-flex flex-column flex-items-stretch pt-2 px-2">
        <Button
          aria-describedby="issue-viewer-subscription-description"
          leadingVisual={subscribed ? BellSlashIcon : BellIcon}
          onClick={toggleSubscription}
          size="small"
        >
          {subscribed ? LABELS.notifications.subscribedButton : LABELS.notifications.unsubscribedButton}
        </Button>
        <p id="issue-viewer-subscription-description" className="my-2 f6 fgColor-muted">
          {subscribed ? LABELS.notifications.subscribedDescription : LABELS.notifications.unsubscribedDescription}
        </p>
      </div>
    </Section>
  )
}

export function SubscriptionSectionFallback() {
  return (
    <Section sectionHeader={<ReadonlySectionHeader title="Notifications" />}>
      <div className="width-full d-flex flex-column flex-items-stretch pt-2 px-2">
        <Button aria-describedby="issue-viewer-subscription-description" leadingVisual={BellIcon}>
          {LABELS.notifications.unsubscribedButton}
        </Button>
        <p id="issue-viewer-subscription-description" className="my-2 f6 fgColor-muted">
          {LABELS.notifications.unsubscribedDescription}
        </p>
      </div>
    </Section>
  )
}
